/**
 * CampaignDrawer — detalle de UNA campaña de Meta (drill-down desde la tabla).
 *
 * Responde la pregunta que sigue a "esta campaña gastó X": ¿cómo está armada
 * (presupuesto, puja, fechas), qué le pasa (aprendizaje, problemas de entrega),
 * en qué conjunto de anuncios se fue el dinero y qué anuncio rindió? Panel
 * lateral para no perder el contexto del tablero; se cierra con Esc o clic fuera.
 */
import React, { useEffect, useState } from 'react';
import {
  FaTimes, FaSpinner, FaExclamationTriangle, FaInfoCircle, FaSearchPlus, FaLayerGroup, FaImages,
} from 'react-icons/fa';
import { metaApi } from '../../services/metaService';
import {
  compact, dec, pct, label, resultLine, shortDate,
  STATUS_LABEL, statusTone, OPT_GOAL_LABEL, BID_LABEL, BUY_LABEL, OBJECTIVE_LABEL,
} from './metaAdsShared';
import { CreativeGrid } from './MetaCreatives';
import './MetaAds.css';

const LEARNING = {
  LEARNING: { text: 'Aprendiendo', tone: 'info' },
  FAIL: { text: 'Aprendizaje limitado', tone: 'warn' },
  SUCCESS: { text: 'Optimizado', tone: 'ok' },
};

export default function CampaignDrawer({ username, account, campaign, range, money, onClose, onFocus }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: null, data: null });
    metaApi.adsCampaign(username, campaign.id, range?.from, range?.to, account)
      .then((d) => { if (!cancelled) setState({ loading: false, error: null, data: d }); })
      .catch((e) => { if (!cancelled) setState({ loading: false, error: e.message || String(e), data: null }); });
    return () => { cancelled = true; };
  }, [username, account, campaign.id, range?.from, range?.to]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const d = state.data;
  const c = d?.campaign || {};
  const t = d?.totals || {};
  const res = resultLine(t.result, money);
  const budget = c.budgetLifetime
    ? `${money(c.budgetLifetime)} total`
    : c.budgetDaily ? `${money(c.budgetDaily)} diarios` : c.budgetLevel === 'adset' ? 'En cada conjunto' : '—';

  return (
    <div className="mad-drawer-overlay" onClick={onClose}>
      <aside className="mad-drawer" role="dialog" aria-modal="true" aria-label={campaign.name} onClick={(e) => e.stopPropagation()}>
        <header className="mad-drawer-head">
          <div className="mad-drawer-title">
            <span className="mad-muted">Campaña de Meta</span>
            <h2>{c.name || campaign.name}</h2>
            <div className="mad-drawer-tags">
              {c.status && <span className={`mad-chip ${statusTone(c.status)}`}>{label(STATUS_LABEL, c.status)}</span>}
              {c.objective && <span className="mad-chip">{label(OBJECTIVE_LABEL, c.objective)}</span>}
              {(c.start || c.stop) && <span className="mad-muted">{shortDate(c.start)} → {c.stop ? shortDate(c.stop) : 'sin fecha de fin'}</span>}
            </div>
          </div>
          <button type="button" className="mad-drawer-close" onClick={onClose} aria-label="Cerrar"><FaTimes /></button>
        </header>

        <div className="mad-drawer-body">
          {state.loading && <div className="mad-loading"><FaSpinner className="cmp-spin" /> Consultando la campaña en Meta…</div>}
          {state.error && <div className="mis-warn"><FaExclamationTriangle /><span>No se pudo cargar el detalle: {state.error}</span></div>}

          {d && (
            <>
              <div className="mad-facts">
                <Fact l="Presupuesto" v={budget} s={c.budgetLevel === 'campaign' ? 'a nivel campaña (CBO)' : c.budgetLevel === 'adset' ? 'a nivel conjunto' : null} />
                {c.budgetLifetime && c.lifetimeSpend != null ? (
                  <Fact l="Ejecutado del presupuesto total" v={pct(c.lifetimeSpend / c.budgetLifetime, 0)}
                    s={`${money(c.lifetimeSpend)} de ${money(c.budgetLifetime)} desde el inicio`}
                    bar={c.lifetimeSpend / c.budgetLifetime} />
                ) : c.budgetRemaining != null && <Fact l="Presupuesto restante" v={money(c.budgetRemaining)} />}
                <Fact l="Estrategia de puja" v={label(BID_LABEL, c.bidStrategy)} />
                <Fact l="Tipo de compra" v={label(BUY_LABEL, c.buyType)} />
              </div>

              {(c.issues || []).map((i, idx) => (
                <div key={idx} className="mis-warn mad-gap"><FaExclamationTriangle /><span>{i}</span></div>
              ))}

              <h3 className="mad-drawer-sec">Rendimiento del periodo <span className="mad-muted">{shortDate(d.period?.since)} – {shortDate(d.period?.until)}</span></h3>
              <div className="mad-kpi-grid">
                <K l="Inversión" v={money(t.spend)} />
                {res ? <K l={res.label} v={res.value} s={res.cost} accent /> : <K l="Impresiones" v={compact(t.impressions)} />}
                {t.result?.key === 'reach'
                  ? <K l="Impresiones" v={compact(t.impressions)} s={t.frequency != null ? `frecuencia ${dec(t.frequency)}` : null} />
                  : <K l="Personas alcanzadas" v={compact(t.reach)} s={t.frequency != null ? `frecuencia ${dec(t.frequency)}` : null} />}
                <K l="CTR" v={pct(t.ctr)} s={t.outboundCtr != null ? `de salida ${pct(t.outboundCtr)}` : null} />
                <K l="CPM" v={money.unit(t.cpm)} s={t.cpc != null ? `CPC ${money.unit(t.cpc)}` : null} />
                {t.roas != null
                  ? <K l="ROAS" v={`${dec(t.roas)}x`} s={`${money(t.purchaseValue)} en ventas`} accent />
                  : t.vtr != null ? <K l="VTR" v={pct(t.vtr)} s="vistas completas" /> : <K l="Interacciones" v={compact(t.interactions)} />}
              </div>

              <h3 className="mad-drawer-sec"><FaLayerGroup /> Conjuntos de anuncios <span className="mad-muted">{d.adsets.length}</span></h3>
              {d.adsets.length === 0 ? <div className="cmp-empty">Sin conjuntos de anuncios.</div> : (
                <div className="cmp-table-scroll mad-table-wrap">
                  <table className="cmp-table mad-adsets">
                    <thead>
                      <tr><th>Conjunto</th><th>Estado</th><th>Optimiza a</th><th className="num">Presupuesto</th><th className="num">Inversión</th><th className="num">Resultado</th><th className="num">Costo/res.</th><th className="num">Frec.</th></tr>
                    </thead>
                    <tbody>
                      {d.adsets.map((a) => {
                        const r = a.result;
                        const lrn = LEARNING[a.learning];
                        return (
                          <tr key={a.id}>
                            <td className="mad-adset-name">
                              <strong title={a.name}>{a.name}</strong>
                              {a.targeting && <span className="mad-muted">{a.targeting}</span>}
                              {(a.issues || []).map((i, idx) => <span key={idx} className="mad-issue"><FaExclamationTriangle /> {i}</span>)}
                            </td>
                            <td>
                              <span className={`mad-chip ${statusTone(a.status)}`}>{label(STATUS_LABEL, a.status)}</span>
                              {lrn && a.status === 'ACTIVE' && <span className={`mad-chip ${lrn.tone}`} title={a.learningMessage || ''}>{lrn.text}</span>}
                            </td>
                            <td>{label(OPT_GOAL_LABEL, a.optimizationGoal)}</td>
                            <td className="num">{a.budgetLifetime ? `${money(a.budgetLifetime)} total` : a.budgetDaily ? `${money(a.budgetDaily)}/día` : '—'}</td>
                            <td className="num">{money(a.spend)}</td>
                            <td className="num">{r?.value != null ? compact(r.value) : '—'}</td>
                            <td className="num">{r?.cost != null ? `${money.unit(r.cost)}${r.perMil ? ' /mil' : ''}` : '—'}</td>
                            <td className="num">{dec(a.frequency)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {d.adsets.some((a) => a.learning === 'FAIL' && a.status === 'ACTIVE') && (
                <p className="mad-foot"><FaInfoCircle /> <b>Aprendizaje limitado</b>: el conjunto no junta suficientes resultados para que Meta optimice. Suele resolverse con más presupuesto, una audiencia más amplia o un objetivo de optimización más frecuente.</p>
              )}

              <h3 className="mad-drawer-sec"><FaImages /> Anuncios</h3>
              {d.ads.length === 0 ? <div className="cmp-empty">Sin anuncios con entrega en el periodo.</div>
                : <CreativeGrid creatives={d.ads} money={money} showCampaign={false} />}

              {(d.warnings || []).length > 0 && (
                <details className="mad-warnings">
                  <summary><FaExclamationTriangle /> Meta no entregó parte del detalle ({d.warnings.length})</summary>
                  <ul>{d.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                </details>
              )}
            </>
          )}
        </div>

        {onFocus && (
          <footer className="mad-drawer-foot">
            <button type="button" className="cmp-btn cmp-btn-accent" onClick={() => onFocus({ id: campaign.id, name: c.name || campaign.name, resultKey: c.resultKey || campaign.resultKey })}>
              <FaSearchPlus /> Ver audiencia, ubicaciones y evolución de esta campaña
            </button>
          </footer>
        )}
      </aside>
    </div>
  );
}

function Fact({ l, v, s, bar }) {
  return (
    <div className="mad-fact">
      <span className="l">{l}</span><span className="v">{v}</span>
      {bar != null && <span className="mad-cap mad-fact-bar"><span className="mad-cap-fill" style={{ width: `${Math.min(100, bar * 100)}%`, background: bar > 1.02 ? '#ef4444' : '#10b981' }} /></span>}
      {s && <span className="s">{s}</span>}
    </div>
  );
}

function K({ l, v, s, accent }) {
  return (
    <div className={`mad-k ${accent ? 'accent' : ''}`}>
      <span className="v">{v}</span>
      <span className="l">{l.replace(/^\w/, (x) => x.toUpperCase())}</span>
      {s && <span className="s">{s}</span>}
    </div>
  );
}
